import { FlyoutFooter } from "@app/shared/Flyouts";
import React, { FC, JSX, useEffect, useState } from "react";
import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons"; 
import { QESButton } from "@app/shared/qes-standards/Button";
import { AccessReportsWizardAccessCriteria } from "@app/access-reports/shared/accessReportsWizardAccessCriteria";
import { AccessReportsWizardProduct } from "@app/access-reports/shared/accessReportsWizardProduct";

interface EditSpecialtyNameProps {
  hideSidePanel: () => void;
  onSaveSpecialtyNameClick: (accessCriteriaId: number, accessCriteriaName: string) => void;
  product: AccessReportsWizardProduct;
  specialty: AccessReportsWizardAccessCriteria;
}

const EditSpecialtyName: FC<EditSpecialtyNameProps> = (props: EditSpecialtyNameProps) => {
  const [specialtyNameValue, setSpecialtyNameValue] = useState("");

  useEffect(() => {
    if (!!props.specialty) {
      setSpecialtyName();
    }
  }, []);

  useEffect(() => {
    setSpecialtyName();
  }, [props.specialty]);

  const getEditSpecialtyNameContent = (editSpecialtyNameErrorText: string, missingPropsErrorText: string): JSX.Element => {
    // only grant the user access to the edit specialty name input box
    // if a product, specialties, and the specific specialty whose name is to be edited exist
    if (!!missingPropsErrorText) {
      return (
        <div className="row">
          <div className="col-12">
            <p className="text-muted">
              <QesIcon iconName={QesIcons.AlertHexagon} description="Error" className="text-danger me-1 mb3" />
              <b>Error: </b>
              {missingPropsErrorText}
            </p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="row">
          <div className="col-12">
            <div className="form-group mb-3">
              <label className="mb-0">
                Specialty Name
              </label>
              <div className="input-group">
                <input
                  className="form-control"
                  id={`access-reports-admin__edit-specialty-name--input-${props.specialty?.id}`}
                  onChange={(e) => onSpecialtyNameInputChange(e)}
                  onKeyPress={(e) => onSpecialtyNameInputKeyPress(e, editSpecialtyNameErrorText)}
                  value={specialtyNameValue}
                />

              </div>
              <div className={`text-danger`}>
                {editSpecialtyNameErrorText} &nbsp;
              </div>
            </div>
          </div>
        </div>
      );
    }
  }

  const getEditSpecialtyNameErrorText = (): string => {
    if (!specialtyNameValue) {
      return "Please enter a specialty name";
    } else {
      const specialtyWithThisName: AccessReportsWizardAccessCriteria = !!props.product && props.product.accessCriterion.find(x => x.name === specialtyNameValue);

      if (!!specialtyWithThisName && props.specialty.id !== specialtyWithThisName.id) {
        return "A specialty with that name already exists";
      } else {
        return "";
      }
    }
  }

  const getMissingPropsErrorText = (): string => {
    if (!props.product) {
      return "No Product Found";
    } else if (!props.product.accessCriterion || !props.product.accessCriterion.length) {
      return "No Specialties Found";
    } else if (!props.specialty) {
      return "No Specialty to Edit";
    } else {
      return "";
    }
  }

  const onSpecialtyNameInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setSpecialtyNameValue(event.target.value);
  }

  const onSpecialtyNameInputKeyPress = (event: React.KeyboardEvent<HTMLInputElement>, editSpecialtyNameErrorText: string): void => {
    // allow for the user to save specialty name on enter key press
    // if the currently-input specialty name is valid
    if (event.key === "Enter" && !editSpecialtyNameErrorText) {
      event.preventDefault();
      props.onSaveSpecialtyNameClick(props.specialty.id, specialtyNameValue)
    }
  }

  const setSpecialtyName = (): void => {
    if (!!props.specialty) {
      setSpecialtyNameValue(props.specialty.name);
    }
  }

  const editSpecialtyNameErrorText: string = getEditSpecialtyNameErrorText();
  const missingPropsErrorText: string = getMissingPropsErrorText();

  return (
    <React.Fragment>
      {getEditSpecialtyNameContent(editSpecialtyNameErrorText, missingPropsErrorText)}

      <FlyoutFooter
        submitButton={
          <QESButton
            className="cursor-pointer ms-3"
            disabled={!!editSpecialtyNameErrorText || !!missingPropsErrorText}
            onClick={() => props.onSaveSpecialtyNameClick(props.specialty.id, specialtyNameValue)}
            qesTestId="access-reports-admin__edit-specialty-name--save"
            qesType="primary"
          >
            Save
          </QESButton>
        }
      />
    </React.Fragment>
  );
}

export { EditSpecialtyName };
